// src/pages/CourseDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Clock, User, Star, Play, ShoppingCart, Heart, Check, BookOpen, Calendar, Users, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const renderMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
};

const CourseDetail = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [cohort, setCohort] = useState(null);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/courses/${id}`);
        if (!response.ok) throw new Error('Course not found');
        const data = await response.json();
        setCourse(data);
        if (data.modules && data.modules.length > 0) {
          setExpandedModules({ 0: true });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [id]);

  useEffect(() => {
    if (!course || course.format !== 'cohort') return;
    fetch(`${process.env.REACT_APP_API_URL}/api/cohorts?courseId=${id}`)
      .then(r => r.json())
      .then(data => setCohort(data[0] || null))
      .catch(err => console.error('Failed to load cohort:', err));
  }, [course, id]);

  const toggleModule = (index) => {
    setExpandedModules(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleEnroll = async () => {
    if (!currentUser) {
      navigate('/login', { state: { from: `/course/${id}` } });
      return;
    }

    try {
      setEnrollLoading(true);
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL;
      const body = { courseId: id };
      if (course.format === 'cohort' && cohort) body.cohortId = cohort.id;

      await axios.post(
        `${apiUrl}/api/users/enroll`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/dashboard');
    } catch (err) {
      console.error("Enrollment failed:", err);
      const msg = err.response?.data?.message;
      alert(msg || "Failed to enroll. Please try again.");
    } finally {
      setEnrollLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 0' }}>
        <div className="container" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="spinner"></div>
          <div style={{ fontSize: '18px', color: 'var(--text-light)', marginTop: '20px' }}>Loading course...</div>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div style={{ padding: '40px 0' }}>
        <div className="container" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '18px', color: 'var(--text-light)', marginBottom: '20px' }}>
            {error || 'Course not found'}
          </div>
          <Link to="/courses" className="btn btn-primary">Back to Courses</Link>
        </div>
      </div>
    );
  }

  const totalLessons = course.modules?.reduce((acc, mod) => acc + (mod.lessons?.length || 0), 0) || 0;

  return (
    <div style={{ padding: '40px 0' }}>
      <div className="container">
        {/* Breadcrumb */}
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', color: 'var(--text-light)', fontSize: '13px', marginBottom: '20px', overflow: 'hidden' }}>
          <Link to="/courses" style={{ color: 'var(--primary)', textDecoration: 'none', flexShrink: 0, fontWeight: '500' }}>Courses</Link>
          <span style={{ flexShrink: 0 }}>›</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{course.title}</span>
        </div>

        <div style={{ display: 'flex', gap: '50px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Main Content */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            {/* Course Header */}
            <div style={{ marginBottom: '30px' }}>
              <span style={{ display: 'inline-block', padding: '4px 12px', background: 'rgba(26, 82, 118, 0.08)', color: 'var(--primary)', borderRadius: '4px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '15px' }}>
                {course.category?.toUpperCase()}
              </span>
              <h1 style={{ fontSize: 'clamp(22px, 4vw, 36px)', lineHeight: '1.25', marginBottom: '15px', color: 'var(--primary)' }}>{course.title}</h1>

              <div style={{ display: 'flex', gap: '20px', marginBottom: '25px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-light)', fontSize: '14px' }}>
                  <Clock size={16} />
                  <span>{totalLessons} Lessons</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-light)', fontSize: '14px' }}>
                  <User size={16} />
                  <span>{course.level}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-light)', fontSize: '14px' }}>
                  <BookOpen size={16} />
                  <span>{course.modules?.length || 0} Modules</span>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px' }}>
                <div style={{ display: 'flex', color: '#f1c40f' }}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} fill={i < Math.floor(course.rating || 0) ? '#f1c40f' : 'none'} />
                  ))}
                </div>
                <span style={{ color: 'var(--text-light)', fontSize: '14px' }}>
                  {course.rating || 0} ({course.numStudents || 0} students)
                </span>
              </div>

              {/* Instructor */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'var(--primary)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '24px' }}>
                  {course.instructor?.charAt(0) || 'I'}
                </div>
                <div>
                  <h4 style={{ fontSize: '18px', marginBottom: '5px' }}>{course.instructor}</h4>
                  <p style={{ color: 'var(--text-light)', fontSize: '14px' }}>Course Instructor</p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="glass-card">
              <div style={{ display: 'flex', borderBottom: '1px solid var(--glass-border)', flexWrap: 'wrap' }}>
                {['overview', 'curriculum'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      padding: '20px 30px',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      fontWeight: '600',
                      borderBottom: `3px solid ${activeTab === tab ? 'var(--primary)' : 'transparent'}`,
                      background: 'none',
                      border: 'none',
                      color: activeTab === tab ? 'var(--primary)' : 'var(--text)'
                    }}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              <div style={{ padding: '30px' }}>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div>
                    <h3 style={{ marginBottom: '20px', color: 'var(--primary)' }}>Course Description</h3>
                    <p style={{ marginBottom: '25px', lineHeight: '1.7', color: 'var(--text)' }} dangerouslySetInnerHTML={{ __html: renderMarkdown(course.description) }} />

                    <h4 style={{ marginBottom: '15px', color: 'var(--primary)' }}>What You'll Learn</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px', marginBottom: '30px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <Check size={16} style={{ color: 'var(--accent)', marginTop: '3px', flexShrink: 0 }} />
                        <span>{totalLessons} comprehensive lessons across {course.modules?.length || 0} modules</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <Check size={16} style={{ color: 'var(--accent)', marginTop: '3px', flexShrink: 0 }} />
                        <span>Practical, hands-on learning experience</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <Check size={16} style={{ color: 'var(--accent)', marginTop: '3px', flexShrink: 0 }} />
                        <span>Certificate of completion</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Curriculum Tab */}
                {activeTab === 'curriculum' && (
                  <div>
                    <h3 style={{ marginBottom: '20px', color: 'var(--primary)' }}>Course Curriculum</h3>
                    <p style={{ marginBottom: '25px', color: 'var(--text-light)' }}>
                      {course.modules?.length || 0} modules • {totalLessons} lessons
                    </p>

                    {course.modules?.map((module, idx) => (
                      <div key={idx} className="glass-card" style={{ marginBottom: '15px', border: '1px solid var(--glass-border)', borderRadius: '15px', overflow: 'hidden' }}>
                        <div
                          style={{
                            padding: '20px',
                            background: 'rgba(255, 255, 255, 0.5)',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                          onClick={() => toggleModule(idx)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'rgba(26, 82, 118, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '14px' }}>
                              <BookOpen size={16} />
                            </div>
                            <div>
                              <div style={{ fontWeight: '600' }}>{module.title}</div>
                              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                                {module.lessons?.length || 0} lesson{module.lessons?.length !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                          <div style={{ color: 'var(--text-light)', fontSize: '14px' }}>
                            {expandedModules[idx] ? '▼' : '▶'}
                          </div>
                        </div>

                        {expandedModules[idx] && module.lessons && module.lessons.length > 0 && (
                          <div>
                            {module.lessons.map((lesson, lessonIdx) => (
                              <div key={lessonIdx} style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '15px 20px', borderTop: '1px solid var(--glass-border)' }}>
                                <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'rgba(26, 82, 118, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '12px', flexShrink: 0 }}>
                                  <Play size={12} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: '500', marginBottom: '3px' }}>{lesson.title}</div>
                                  <div style={{ display: 'flex', gap: '15px', color: 'var(--text-light)', fontSize: '12px' }}>
                                    <span>video</span>
                                    <span>{lesson.duration || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ width: '400px', minWidth: '300px' }}>
            <div className="glass-card" style={{ padding: '30px', position: 'sticky', top: '100px' }}>
              {/* Course Image */}
              {course.image ? (
                <div style={{ width: '100%', height: '200px', backgroundImage: `url(${course.image})`, backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: '10px', marginBottom: '20px' }} />
              ) : (
                <div style={{ width: '100%', height: '200px', background: 'linear-gradient(135deg, var(--primary-light) 0%, var(--accent) 100%)', borderRadius: '10px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '48px' }}>
                  📚
                </div>
              )}

              {course.format === 'cohort' ? (
                /* ── Cohort sidebar ── */
                <>
                  {cohort ? (
                    <>
                      {/* Start date */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                        <Calendar size={18} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '2px' }}>Cohort starts</div>
                          <div style={{ fontWeight: '700', color: 'var(--text)', fontSize: '16px' }}>
                            {new Date(cohort.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </div>
                        </div>
                      </div>

                      {/* Seats remaining */}
                      {(() => {
                        const remaining = cohort.maxSeats - cohort.enrolledCount;
                        const low = remaining <= 5;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', padding: '12px 14px', borderRadius: '8px', background: low ? 'rgba(231,76,60,0.06)' : 'rgba(26,82,118,0.05)', border: `1px solid ${low ? 'rgba(231,76,60,0.2)' : 'var(--border)'}` }}>
                            {low ? <AlertTriangle size={16} style={{ color: '#e74c3c', flexShrink: 0 }} /> : <Users size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />}
                            <span style={{ fontSize: '14px', fontWeight: '600', color: low ? '#e74c3c' : 'var(--text)' }}>
                              {remaining} seat{remaining !== 1 ? 's' : ''} remaining
                              {low && ' — filling fast'}
                            </span>
                          </div>
                        );
                      })()}

                      {/* Price */}
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ fontSize: '30px', fontWeight: '700', color: 'var(--primary)', marginBottom: '4px' }}>
                          GHS {course.price}
                        </div>
                        <div style={{ color: 'var(--text-light)', fontSize: '13px' }}>Cohort access • {Math.round((new Date(cohort.endDate) - new Date(cohort.startDate)) / (1000 * 60 * 60 * 24 * 7))} weeks</div>
                      </div>

                      <ul style={{ listStyle: 'none', marginBottom: '24px' }}>
                        {[`${totalLessons} structured lessons`, 'Peer cohort accountability', 'Live instructor support', 'Certificate of completion'].map(item => (
                          <li key={item} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 0', color: 'var(--text-light)', borderBottom: '1px solid var(--border)' }}>
                            <Check size={15} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                            <span style={{ fontSize: '14px' }}>{item}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={handleEnroll}
                        className="btn btn-primary"
                        style={{ width: '100%', marginBottom: '12px', justifyContent: 'center', padding: '12px' }}
                        disabled={enrollLoading || cohort.enrolledCount >= cohort.maxSeats}
                      >
                        {enrollLoading ? 'Joining...' : cohort.enrolledCount >= cohort.maxSeats ? 'Cohort Full' : 'Join Cohort'}
                      </button>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-light)', fontSize: '14px' }}>
                      No open cohort available right now. Check back soon.
                    </div>
                  )}
                </>
              ) : (
                /* ── Self-paced sidebar ── */
                <>
                  <div style={{ marginBottom: '25px' }}>
                    <div style={{ fontSize: '30px', fontWeight: '700', color: 'var(--primary)', marginBottom: '4px' }}>
                      GHS {course.price}
                      {course.originalPrice && (
                        <>
                          <span style={{ fontSize: '18px', color: 'var(--text-light)', textDecoration: 'line-through', marginLeft: '10px' }}>
                            GHS {course.originalPrice}
                          </span>
                          <span style={{ background: 'var(--accent)', color: 'white', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', marginLeft: '10px' }}>
                            {Math.round((1 - course.price / course.originalPrice) * 100)}% OFF
                          </span>
                        </>
                      )}
                    </div>
                    <div style={{ color: 'var(--text-light)', fontSize: '13px' }}>One-time payment • Lifetime access</div>
                  </div>

                  <ul style={{ listStyle: 'none', marginBottom: '24px' }}>
                    {[`${totalLessons} video lessons`, 'Lifetime access', 'Certificate of completion'].map(item => (
                      <li key={item} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 0', color: 'var(--text-light)', borderBottom: '1px solid var(--border)' }}>
                        <Check size={15} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                        <span style={{ fontSize: '14px' }}>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={handleEnroll}
                    className="btn btn-primary"
                    style={{ width: '100%', marginBottom: '12px', justifyContent: 'center', padding: '12px' }}
                    disabled={enrollLoading}
                  >
                    <ShoppingCart size={18} />
                    {enrollLoading ? 'Enrolling...' : 'Enroll Now'}
                  </button>
                  <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
                    <Heart size={18} />
                    Add to Wishlist
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;